/**
 * Subscriptions Page
 * Manages user subscription plans with PayPal integration
 */

import { useState, useEffect } from 'react'
import { Check, Star, Zap, AlertCircle, Loader2, XCircle } from 'lucide-react'
import { 
  SUBSCRIPTION_PLANS, 
  getActiveSubscription, 
  createSubscription,
  cancelSubscription,
  getDaysRemaining,
  formatPrice 
} from '../../services/subscriptionService'
import PayPalButton from '../../components/PayPalButton'

export default function Subscriptions() {
  const [currentSubscription, setCurrentSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [billingCycle, setBillingCycle] = useState('monthly') // 'monthly' | 'annual'
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [cancelConfirm, setCancelConfirm] = useState(false)

  useEffect(() => {
    loadSubscription()
  }, [])

  const loadSubscription = async () => {
    try {
      setLoading(true)
      const subscription = await getActiveSubscription()
      setCurrentSubscription(subscription)
    } catch (err) {
      console.error('Error loading subscription:', err)
      setError('Failed to load subscription details')
    } finally {
      setLoading(false)
    }
  }

  const handleSubscriptionSuccess = async (details, planName) => {
    try {
      setLoading(true)
      setError(null)

      // Create subscription in database
      const plan = SUBSCRIPTION_PLANS[planName]
      const effectivePrice = billingCycle === 'annual' ? plan.annualPrice : plan.price
      const subscriptionData = {
        plan_name: planName,
        plan_price: effectivePrice,
        billing_cycle: billingCycle,
        status: 'active',
        paypal_subscription_id: details.subscriptionID || null,
        paypal_order_id: details.orderID || details.id,
        paypal_payer_id: details.payer?.payer_id || null,
        paypal_email: details.payer?.email_address || null,
        metadata: {
          paypal_details: details
        }
      }

      await createSubscription(subscriptionData)
      
      setSuccess(`Successfully subscribed to ${plan.name}!`)
      setSelectedPlan(null)
      
      // Reload subscription
      await loadSubscription()

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000)
    } catch (err) {
      console.error('Error creating subscription:', err)
      setError('Failed to activate subscription. Please contact support.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubscriptionError = (err) => {
    console.error('PayPal error:', err)
    setError('Payment failed. Please try again.')
  }

  const handleCancelSubscription = async () => {
    if (!currentSubscription) return
    try {
      setLoading(true)
      setError(null)
      setCancelConfirm(false)
      await cancelSubscription(currentSubscription.id)
      setSuccess('Subscription cancelled successfully.')
      await loadSubscription()
      setTimeout(() => setSuccess(null), 6000)
    } catch (err) {
      console.error('Error cancelling subscription:', err)
      setError('Failed to cancel subscription. Please contact support.')
    } finally {
      setLoading(false)
    }
  }

  if (loading && !currentSubscription) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-accent" />
      </div>
    )
  }

  const daysRemaining = currentSubscription ? getDaysRemaining(currentSubscription) : 0
  const currentPlan = currentSubscription ? SUBSCRIPTION_PLANS[currentSubscription.plan_name] : null

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-background-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12">
        
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Subscription Plans
          </h1>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-6">
            Choose the perfect plan for your creative workflow
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex p-1 bg-gray-200 dark:bg-gray-800 rounded-xl">
            <button
              onClick={() => { setBillingCycle('monthly'); setSelectedPlan(null) }}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white dark:bg-surface-dark text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => { setBillingCycle('annual'); setSelectedPlan(null) }}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-white dark:bg-surface-dark text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Annual
              <span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-5 sm:mb-6 p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2 sm:gap-3">
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-5 sm:mb-6 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2 sm:gap-3">
            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-green-800 dark:text-green-200">{success}</p>
            </div>
          </div>
        )}

        {/* Current Subscription Card */}
        {currentSubscription && (
          <div className="mb-8 sm:mb-10 lg:mb-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-5 sm:p-6 lg:p-8 text-white shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <p className="text-xs sm:text-sm font-medium text-emerald-100 mb-1 sm:mb-2">Current Plan</p>
                <h2 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">{currentPlan?.name}</h2>
                <p className="text-base sm:text-lg text-emerald-50">
                  {formatPrice(currentSubscription.plan_price)}/month
                </p>
              </div>
              <div className="sm:text-right">
                <p className="text-xs sm:text-sm text-emerald-100 mb-1">Status</p>
                <span className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm">
                  Active
                </span>
              </div>
            </div>
            
            <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-white/20">
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <p className="text-xs sm:text-sm text-emerald-100 mb-1">Next Billing</p>
                  <p className="font-semibold text-sm sm:text-base">
                    {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-emerald-100 mb-1">Days Remaining</p>
                  <p className="font-semibold text-sm sm:text-base">{daysRemaining} days</p>
                </div>
              </div>

              {/* Cancel Subscription */}
              <div className="mt-4 sm:mt-5">
                {cancelConfirm ? (
                  <div className="bg-black/20 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-white mb-3">
                      Are you sure? You will lose access at the end of the billing period.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCancelSubscription}
                        disabled={loading}
                        className="flex-1 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs sm:text-sm font-semibold transition-colors disabled:opacity-60"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Yes, Cancel'}
                      </button>
                      <button
                        onClick={() => setCancelConfirm(false)}
                        className="flex-1 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs sm:text-sm font-semibold transition-colors"
                      >
                        Keep Plan
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setCancelConfirm(true)}
                    className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Cancel subscription
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
          {/* Start Plan */}
          <div className={`bg-white dark:bg-surface-dark rounded-2xl shadow-lg border-2 transition-all ${
            currentSubscription?.plan_name === 'start'
              ? 'border-accent ring-4 ring-accent/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-accent/50'
          }`}>
            <div className="p-5 sm:p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Star className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {SUBSCRIPTION_PLANS.start.name}
                </h3>
              </div>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  {billingCycle === 'annual' ? SUBSCRIPTION_PLANS.start.annualPriceDisplay : SUBSCRIPTION_PLANS.start.priceDisplay}
                </span>
                <span className="text-sm sm:text-base text-gray-500 dark:text-gray-400">/mo</span>
                {billingCycle === 'annual' && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1">
                    ${SUBSCRIPTION_PLANS.start.annualTotal} billed annually
                  </p>
                )}
              </div>

              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {SUBSCRIPTION_PLANS.start.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 sm:gap-3">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {currentSubscription?.plan_name === 'start' ? (
                <button
                  disabled
                  className="w-full py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-semibold cursor-not-allowed text-sm sm:text-base"
                >
                  Current Plan
                </button>
              ) : selectedPlan === 'start' ? (
                <div>
                  <PayPalButton
                    planName="start"
                    amount={billingCycle === 'annual' ? SUBSCRIPTION_PLANS.start.annualPrice : SUBSCRIPTION_PLANS.start.price}
                    billingCycle={billingCycle}
                    onSuccess={(details) => handleSubscriptionSuccess(details, 'start')}
                    onError={handleSubscriptionError}
                  />
                  <button
                    onClick={() => setSelectedPlan(null)}
                    className="w-full mt-2 py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setSelectedPlan('start')}
                  className="w-full py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg bg-accent hover:bg-accent/90 text-white font-semibold transition-colors text-sm sm:text-base"
                >
                  Choose Start Plan
                </button>
              )}
            </div>
          </div>

          {/* Pro Plan */}
          <div className={`bg-white dark:bg-surface-dark rounded-2xl shadow-lg border-2 transition-all relative ${
            currentSubscription?.plan_name === 'pro' 
              ? 'border-accent ring-4 ring-accent/20' 
              : 'border-accent hover:border-accent/80'
          }`}>
            {/* Popular Badge */}
            <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1 px-3 sm:px-4 py-1 rounded-full bg-accent text-white text-xs sm:text-sm font-semibold shadow-lg">
                <Star className="w-3 h-3 sm:w-4 sm:h-4" />
                Most Popular
              </span>
            </div>

            <div className="p-5 sm:p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {SUBSCRIPTION_PLANS.pro.name}
                </h3>
              </div>
              
              <div className="mb-5 sm:mb-6">
                <span className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                  {billingCycle === 'annual' ? SUBSCRIPTION_PLANS.pro.annualPriceDisplay : SUBSCRIPTION_PLANS.pro.priceDisplay}
                </span>
                <span className="text-sm sm:text-base text-gray-500 dark:text-gray-400">/mo</span>
                {billingCycle === 'annual' && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1">
                    ${SUBSCRIPTION_PLANS.pro.annualTotal} billed annually
                  </p>
                )}
              </div>

              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {SUBSCRIPTION_PLANS.pro.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 sm:gap-3">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {currentSubscription?.plan_name === 'pro' ? (
                <button
                  disabled
                  className="w-full py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-semibold cursor-not-allowed text-sm sm:text-base"
                >
                  Current Plan
                </button>
              ) : selectedPlan === 'pro' ? (
                <div>
                  <PayPalButton
                    planName="pro"
                    amount={billingCycle === 'annual' ? SUBSCRIPTION_PLANS.pro.annualPrice : SUBSCRIPTION_PLANS.pro.price}
                    billingCycle={billingCycle}
                    onSuccess={(details) => handleSubscriptionSuccess(details, 'pro')}
                    onError={handleSubscriptionError}
                  />
                  <button
                    onClick={() => setSelectedPlan(null)}
                    className="w-full mt-2 py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                </div>  
              ) : (
                <button
                  onClick={() => setSelectedPlan('pro')}
                  className="w-full py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg bg-accent hover:bg-accent/90 text-white font-semibold transition-colors text-sm sm:text-base"
                >
                  Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 sm:mt-14 lg:mt-16 max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-5 sm:mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3 sm:space-y-4">
            <details className="group bg-white dark:bg-surface-dark rounded-lg p-4 sm:p-5 lg:p-6 border border-gray-200 dark:border-gray-700">
              <summary className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white cursor-pointer list-none flex items-center justify-between">
                <span>Can I change plans anytime?</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </details>
            
            <details className="group bg-white dark:bg-surface-dark rounded-lg p-4 sm:p-5 lg:p-6 border border-gray-200 dark:border-gray-700">
              <summary className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white cursor-pointer list-none flex items-center justify-between">
                <span>What payment methods do you accept?</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                We accept all major payment methods through PayPal, including credit cards, debit cards, and PayPal balance.
              </p>
            </details>

            <details className="group bg-white dark:bg-surface-dark rounded-lg p-4 sm:p-5 lg:p-6 border border-gray-200 dark:border-gray-700">
              <summary className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white cursor-pointer list-none flex items-center justify-between">
                <span>Is my payment information secure?</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Absolutely! All payments are processed securely through PayPal. We never store your payment details.
              </p>
            </details>
          </div>
        </div>
      </div>
    </div>
  )
}
